import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function TrainingSourceRegisterPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canViewTraining, canManageTrainingContent } = usePermissions();
  const [sources, setSources] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ source_type: "MANUAL", language: "en", store: "", notes: "" });
  const [uploading, setUploading] = useState(false);

  const load = () => trainingApi.listSources().then((r) => setSources(r.data)).catch(() => {});

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="training-sources-access-denied">{t("common.accessDenied")}</p>;

  const upload = async () => {
    if (!file) { toast.error(t("mgmtTrainingSource.chooseFileFirst")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_type", form.source_type);
      fd.append("language", form.language);
      if (form.store) fd.append("store", form.store);
      if (form.notes) fd.append("notes", form.notes);
      await trainingApi.uploadSource(fd);
      toast.success(t("mgmtTrainingSource.sourceRegistered"));
      setFile(null); setForm({ ...form, notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-5" data-testid="training-sources-page">
      <PageHelpBanner pageKey="training-sources" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtTrainingSource.help")}</p>
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTrainingSource.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTrainingSource.subtitle")}</p>
      </div>

      {canManageTrainingContent && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="upload-source-form">
          <input data-testid="source-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm col-span-2" />
          <select data-testid="source-type-select" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
            <option value="MANUAL">{t("mgmtTrainingSource.typeManual")}</option>
            <option value="POLICY">{t("mgmtTrainingSource.typePolicy")}</option>
            <option value="SOP">{t("mgmtTrainingSource.typeSop")}</option>
            <option value="OTHER">{t("mgmtPayroll.other")}</option>
          </select>
          <input data-testid="source-store-input" value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })} placeholder={t("mgmtTrainingSource.storeOptional")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <input data-testid="source-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t("mgmtWorkCalendarDetail.notes")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
          <button data-testid="upload-source-btn" disabled={uploading} onClick={upload} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 col-span-2 md:col-span-1">
            {uploading ? t("mgmtTrainingSource.uploadingEllipsis") : t("mgmtTrainingSource.uploadSource")}
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtTrainingSource.colSourceId")}</th><th className="px-4 py-3 font-medium">{t("mgmtTrainingSource.colFilename")}</th><th className="px-4 py-3 font-medium">{t("mgmtDataQuality.type")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-t border-gray-100" data-testid={`source-row-${s.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{s.source_id}</td>
                <td className="px-4 py-2 text-gray-600">{s.original_filename}</td>
                <td className="px-4 py-2 text-gray-600">{s.source_type}</td>
                <td className="px-4 py-2"><StatusBadge status={s.review_status} /></td>
                <td className="px-4 py-2"><button data-testid={`open-source-btn-${s.id}`} onClick={() => navigate(`/management/training/sources/${s.id}`)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtContentReview.reviewArrow")}</button></td>
              </tr>
            ))}
            {sources.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-sources-row">{t("mgmtTrainingSource.noSourcesYet")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
